import { AlertTriangle, ArrowLeft, CheckCircle2, RefreshCw, Scale, Search, ShieldCheck, Wallet } from "lucide-react";
import { useEffect, useState, type FormEvent } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Modal } from "../../../components/Modal";
import { Spinner } from "../../../components/Spinner";
import { usePageHeader } from "../../../components/shell/PageHeaderContext";
import { apiRequest } from "../../../lib/api";
import { formatTimestamp, money, relativeTime } from "../../../lib/format";
import { useFundOverview, useFundWallet, useFundWallets, useInvalidateAll } from "../../../lib/queries";
import type { FundBreak, FundBreakTarget, FundJournalEntry, FundOverview, FundPots, FundRunStatus, FundVerification, FundWalletDetail, FundWalletRow } from "../../../types";
import { EmptyRow, Feedback, Field, KeyValues, PageError, Pill, StatTile, useAction } from "../shared";

/**
 * Fondos — where every peso Kuspit holds for a customer is, to the centavo.
 *
 * Kuspit keeps one contract and one fund per customer; the platform splits that single figure into
 * pots: Wallet Libre, one per Apartado, withdrawals in flight, money applied to a trip, and a
 * suspense pot for what nobody has explained yet. This screen shows the split, whether it adds up
 * to what Kuspit reports, and lets an administrator explain a difference with a recorded adjustment.
 */

const FUND_BASE = "/admin/fondos";

const RUN_LABELS: Record<FundRunStatus, { label: string; tone: "ok" | "warn" | "bad" | "info" }> = {
  matched: { label: "cuadra", tone: "ok" },
  opened: { label: "libro abierto", tone: "info" },
  pending_window: { label: "retiro en curso", tone: "warn" },
  break: { label: "diferencia", tone: "bad" }
};

const KIND_LABELS: Record<string, string> = {
  opening_balance: "Apertura del libro", deposit: "Depósito", withdrawal_hold: "Retiro instruido", withdrawal_settled: "Retiro liquidado",
  withdrawal_released: "Retiro no ejecutado", yield: "Rendimiento", refund: "Devolución", apartado_contribution: "Aportación a Apartado",
  apartado_release: "Apartado liberado", apartado_applied: "Apartado aplicado al viaje", apartado_reversal: "Reversión de disposición", adjustment: "Ajuste"
};

const ACCOUNT_LABELS: Record<string, string> = {
  libre: "Wallet Libre", pending_out: "Retiros en curso", applied: "Aplicado al viaje", suspense: "Suspense",
  "external:spei_in": "SPEI entrante", "external:spei_out": "SPEI saliente", "external:yield": "Rendimientos Kuspit",
  "external:opening": "Saldo inicial Kuspit", "external:adjustment": "Ajuste administrativo", "external:trip_settlement": "Pago de viaje (operaciones)"
};

const signed = (value: number) => `${value > 0 ? "+" : value < 0 ? "−" : ""}${money.format(Math.abs(value))}`;
const accountLabel = (code: string, tripNames?: Map<string, string | null>) =>
  code.startsWith("apartado:") ? `Apartado · ${tripNames?.get(code.slice(9)) ?? code.slice(9, 17)}` : ACCOUNT_LABELS[code] ?? code;

function RunPill({ status }: { status: FundRunStatus }) {
  const meta = RUN_LABELS[status];
  return <Pill tone={meta.tone} label={meta.label} />;
}

function DifferencePill({ pots }: { pots: FundPots }) {
  if (!pots.opened) return <Pill tone="muted" label="libro sin abrir" />;
  if (pots.difference === null) return <Pill tone="muted" label="sin lectura de Kuspit" />;
  if (pots.difference === 0) return <Pill tone="ok" label="cuadra" />;
  return <Pill tone="bad" label={`diff ${signed(pots.difference)}`} />;
}

// --- Overview + list --------------------------------------------------------------------------
const VIEWS = [
  { value: "", label: "Todas" }, { value: "breaks", label: "Con diferencia" }, { value: "pending", label: "Retiros en curso" },
  { value: "suspense", label: "Con suspense" }, { value: "unopened", label: "Libro sin abrir" }
];

export function AdminFundPage() {
  usePageHeader("Fondos", "Dónde está cada peso que Kuspit custodia: Wallet Libre, Apartados, retiros en curso y lo aplicado a viajes.");
  const overview = useFundOverview();
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get("q") ?? "");
  useEffect(() => { setSearch(params.get("q") ?? ""); }, [params]);
  const solo = params.get("solo") ?? "";
  const wallets = useFundWallets({ q: search.trim() || undefined, solo: solo || undefined });
  const setView = (value: string) => {
    const next = new URLSearchParams(params);
    if (value) next.set("solo", value); else next.delete("solo");
    setParams(next);
  };
  return (
    <div className="page-container admin-stack">
      {overview.isLoading && <div className="page-state"><Spinner label="Cargando" /></div>}
      {overview.isError && <PageError title="No pudimos cargar el estado de los fondos" retry={() => void overview.refetch()} />}
      {overview.data && <OverviewBody data={overview.data} />}

      <section className="card">
        <div className="section-heading small"><div><span className="page-eyebrow">Por contrato</span><h2>Bolsillos de cada wallet</h2></div></div>
        <div className="discover-toolbar">
          <label className="search-field">
            <Search size={18} aria-hidden="true" />
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Nombre, correo o contrato" aria-label="Buscar wallets" />
          </label>
          <div className="chip-row" role="tablist" aria-label="Filtrar wallets">
            {VIEWS.map((view) => (
              <button key={view.value || "all"} type="button" role="tab" aria-selected={solo === view.value} className={`chip${solo === view.value ? " active" : ""}`} onClick={() => setView(view.value)}>{view.label}</button>
            ))}
          </div>
          <span className="toolbar-count">{wallets.data?.length ?? 0} wallets</span>
        </div>
        {wallets.isLoading && <div className="page-state"><Spinner label="Cargando wallets" /></div>}
        {wallets.isError && <PageError title="No pudimos cargar las wallets" retry={() => void wallets.refetch()} />}
        {wallets.data && (
          <div className="table-wrap">
            <table className="admin-table">
              <thead>
                <tr><th>Cliente</th><th>Kuspit</th><th>Wallet Libre</th><th>Apartados</th><th>En curso · aplicado</th><th>Conciliación</th></tr>
              </thead>
              <tbody>
                {wallets.data.map((row) => <WalletRow key={row.id} row={row} />)}
                {wallets.data.length === 0 && <EmptyRow colSpan={6}>Sin wallets{solo ? " para este filtro" : ""}.</EmptyRow>}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function OverviewBody({ data }: { data: FundOverview }) {
  const action = useAction();
  const [verification, setVerification] = useState<FundVerification | null>(null);
  const verify = () => action.run("verify", async () => {
    const result = await apiRequest<{ data: FundVerification }>("/api/v1/admin/fondos/verify", { method: "POST" });
    setVerification(result.data);
    return result.data.ok
      ? `Cadena íntegra: ${result.data.entries} asientos, ${result.data.postings} movimientos y ${result.data.accounts} cuentas verificados.`
      : `La verificación FALLÓ: ${result.data.failure?.kind} — ${result.data.failure?.detail}`;
  });
  const balanced = data.difference === 0;
  return (
    <>
      <div className="stat-grid four">
        <StatTile label="Saldo total en Kuspit" value={money.format(data.provider.total)} hint={data.provider.asOf ? `última lectura ${relativeTime(data.provider.asOf)}` : "sin lecturas"} icon={Wallet} />
        <StatTile label="Suma de bolsillos" value={money.format(data.pots.total)} hint={`${data.wallets.opened} de ${data.wallets.withContract} contratos con libro`} icon={Scale} tone={balanced ? "ok" : "alert"} />
        <StatTile label="Diferencia global" value={signed(data.difference)} hint={balanced ? "Kuspit y los bolsillos coinciden al centavo" : "Kuspit menos bolsillos, todas las wallets"} icon={balanced ? CheckCircle2 : AlertTriangle} tone={balanced ? "ok" : "danger"} />
        <StatTile label="Diferencias abiertas" value={data.breaks.open} hint={data.breaks.open ? `${signed(data.breaks.openAmount)} · la más antigua ${data.breaks.oldestOpenAt ? relativeTime(data.breaks.oldestOpenAt) : ""}` : `${data.breaks.resolved30d} resueltas · ${data.breaks.cleared30d} aclaradas solas (30 d)`} icon={AlertTriangle} tone={data.breaks.open ? "danger" : "ok"} to={`${FUND_BASE}?solo=breaks`} />
      </div>
      <div className="admin-two-col">
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Bifurcación</span><h2>En qué está el dinero</h2></div></div>
          <KeyValues rows={[
            { label: "Wallet Libre (disponible)", value: money.format(data.pots.libre) },
            { label: "Apartados (destinado a viajes)", value: money.format(data.pots.apartados) },
            { label: "Retiros en curso", value: money.format(data.pots.pendingOut) },
            { label: "Aplicado a viajes (aún en Kuspit)", value: money.format(data.pots.applied) },
            { label: "Suspense (sin explicar)", value: <span className={data.pots.suspense !== 0 ? "bad" : undefined}>{signed(data.pots.suspense)}</span> },
            { label: "Total", value: <strong>{money.format(data.pots.total)}</strong> }
          ]} />
          <p className="field-hint">Cada movimiento de dinero es un asiento de doble entrada en centavos enteros; los bolsillos de un cliente nunca son negativos y ningún asiento se edita ni se borra. Kuspit sigue siendo el saldo de referencia: los bolsillos lo explican.</p>
        </section>
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Integridad</span><h2>Conciliación y cadena</h2></div></div>
          <KeyValues rows={[
            { label: "Última conciliación", value: data.lastRunAt ? relativeTime(data.lastRunAt) : "—" },
            { label: "Corridas en 24 h", value: `${data.runs24h.matched ?? 0} cuadran · ${data.runs24h.pending_window ?? 0} retiro en curso · ${data.runs24h.break ?? 0} diferencia · ${data.runs24h.opened ?? 0} apertura` },
            { label: "Contratos sin libro", value: data.wallets.unopened ? <Link to={`${FUND_BASE}?solo=unopened`}><Pill tone="warn" label={String(data.wallets.unopened)} /></Link> : <Pill tone="ok" label="0" /> },
            verification ? { label: "Verificación", value: <span className={verification.ok ? "ok" : "bad"}>{verification.ok ? `íntegra · ${verification.entries} asientos` : `FALLÓ · ${verification.failure?.detail}`} · {formatTimestamp(verification.checkedAt)}</span> } : null
          ]} />
          <div className="row-actions">
            <button type="button" className="secondary-button small" onClick={() => void verify()} disabled={action.busy !== null} title="Recalcular la cadena de hashes y todos los saldos">
              {action.busy === "verify" ? <Spinner /> : <ShieldCheck size={13} aria-hidden="true" />} Verificar cadena
            </button>
          </div>
          <Feedback error={action.error} notice={action.notice} />
          <p className="field-hint">La verificación recalcula el hash de cada asiento contra su predecesor, comprueba que cada asiento suma cero y que el saldo de cada bolsillo es la suma de sus movimientos. También corre por consola con <code>npm run fund:verify</code>.</p>
        </section>
      </div>
    </>
  );
}

function WalletRow({ row }: { row: FundWalletRow }) {
  return (
    <tr className={row.openBreak ? "row-bad" : undefined}>
      <td>
        <Link to={`${FUND_BASE}/${row.id}`} className="table-primary">
          <strong>{row.owner.fullName}</strong>
          <span>{row.owner.email}{row.accountRef ? ` · ${row.accountRef}` : ""}</span>
        </Link>
      </td>
      <td>
        {row.provider ? (
          <div className="table-primary"><strong translate="no">{money.format(row.provider.available)}</strong><span>{row.provider.asOf ? relativeTime(row.provider.asOf) : ""}</span></div>
        ) : <span className="muted">sin lectura</span>}
      </td>
      <td><div className="table-primary"><strong translate="no">{money.format(row.pots.libre)}</strong><span translate="no">disponible {money.format(row.pots.spendable)}</span></div></td>
      <td translate="no">{money.format(row.pots.apartados)}</td>
      <td translate="no">{money.format(row.pots.pendingOut)} · {money.format(row.pots.applied)}{row.pots.suspense !== 0 ? <span className="bad"> · suspense {signed(row.pots.suspense)}</span> : null}</td>
      <td>
        <div className="table-primary">
          <span><DifferencePill pots={row.pots} /></span>
          <span>{row.openBreak ? `abierta ${relativeTime(row.openBreak.firstSeenAt)} · ${row.openBreak.occurrences} lecturas` : row.lastRun ? `${RUN_LABELS[row.lastRun.status].label} · ${relativeTime(row.lastRun.at)}` : "sin conciliar"}</span>
        </div>
      </td>
    </tr>
  );
}

// --- Detail -----------------------------------------------------------------------------------
export function AdminFundWalletPage() {
  const { id } = useParams();
  const detail = useFundWallet(id);
  const owner = detail.data?.wallet.owner;
  usePageHeader(owner ? `Fondos · ${owner.fullName}` : "Fondos", owner ? `${owner.email}${detail.data?.wallet.accountRef ? ` · contrato ${detail.data.wallet.accountRef}` : ""}` : "");
  return (
    <div className="page-container admin-stack">
      <Link to={FUND_BASE} className="text-link"><ArrowLeft size={14} aria-hidden="true" /> Todas las wallets</Link>
      {detail.isLoading && <div className="page-state"><Spinner label="Cargando" /></div>}
      {detail.isError && <PageError title="No pudimos cargar esta wallet" retry={() => void detail.refetch()} />}
      {detail.data && <DetailBody data={detail.data} />}
    </div>
  );
}

function DetailBody({ data }: { data: FundWalletDetail }) {
  const { wallet, pots, journal, runs, breaks } = data;
  const action = useAction();
  const invalidate = useInvalidateAll();
  const tripNames = new Map(pots.byApartado.map((pot) => [pot.apartadoId, pot.tripName]));
  const reconcile = () => action.run("reconcile", async () => {
    const result = await apiRequest<{ data: { reconciliation: { status: FundRunStatus; provider: number; ledger: number; difference: number; yieldPosted: number } } }>(`/api/v1/admin/fondos/wallets/${wallet.id}/reconcile`, { method: "POST" });
    await invalidate();
    const r = result.data.reconciliation;
    return `Kuspit reporta ${money.format(r.provider)}; los bolsillos suman ${money.format(r.ledger)} (${RUN_LABELS[r.status].label}${r.difference ? `, diferencia ${signed(r.difference)}` : ""}${r.yieldPosted ? `, ${money.format(r.yieldPosted)} de rendimiento registrado` : ""}).`;
  });
  const open = () => action.run("open", async () => {
    await apiRequest(`/api/v1/admin/fondos/wallets/${wallet.id}/open`, { method: "POST" });
    await invalidate();
    return "Libro abierto desde el último saldo reportado por Kuspit.";
  });
  const openBreak = breaks.find((item) => item.status === "open") ?? null;
  return (
    <>
      <div className="stat-grid four">
        <StatTile label="Kuspit reporta" value={wallet.provider ? money.format(wallet.provider.available) : "—"} hint={wallet.provider ? `al ${formatTimestamp(wallet.provider.asOf)}` : "sin lectura"} icon={Wallet} />
        <StatTile label="Suma de bolsillos" value={money.format(pots.total)} hint={pots.opened ? "libre + apartados + en curso + aplicado + suspense" : "libro sin abrir"} icon={Scale} />
        <StatTile label="Diferencia" value={pots.difference === null ? "—" : signed(pots.difference)} hint={pots.difference === 0 ? "cuadra al centavo" : openBreak ? `abierta ${relativeTime(openBreak.firstSeenAt)}` : ""} icon={pots.difference === 0 ? CheckCircle2 : AlertTriangle} tone={pots.difference === 0 ? "ok" : pots.difference === null ? "default" : "danger"} />
        <StatTile label="Disponible para el cliente" value={money.format(pots.spendable)} hint="lo que puede retirar o apartar ahora" icon={CheckCircle2} />
      </div>

      <div className="admin-two-col">
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Bolsillos</span><h2>Bifurcación del contrato</h2></div></div>
          <KeyValues rows={[
            { label: "Wallet Libre", value: money.format(pots.libre) },
            ...pots.byApartado.map((pot) => ({ label: `Apartado · ${pot.tripName ?? pot.apartadoId.slice(0, 8)}`, value: money.format(pot.balance) })),
            { label: "Retiros en curso", value: money.format(pots.pendingOut) },
            { label: "Aplicado a viajes (aún en Kuspit)", value: money.format(pots.applied) },
            { label: "Suspense", value: <span className={pots.suspense !== 0 ? "bad" : undefined}>{signed(pots.suspense)}</span> },
            { label: "Total", value: <strong>{money.format(pots.total)}</strong> }
          ]} />
          <div className="row-actions">
            {wallet.accountRef && (
              <button type="button" className="secondary-button small" onClick={() => void reconcile()} disabled={action.busy !== null} title="Consultar Kuspit ahora y conciliar">
                {action.busy === "reconcile" ? <Spinner /> : <RefreshCw size={13} aria-hidden="true" />} Conciliar ahora
              </button>
            )}
            {!pots.opened && wallet.provider && (
              <button type="button" className="secondary-button small" onClick={() => void open()} disabled={action.busy !== null} title="Abrir el libro desde el último saldo reportado">
                {action.busy === "open" ? <Spinner /> : null} Abrir libro desde el último saldo
              </button>
            )}
          </div>
          <Feedback error={action.error} notice={action.notice} />
        </section>
        <section className="card">
          <div className="section-heading small"><div><span className="page-eyebrow">Diferencias</span><h2>Kuspit vs bolsillos</h2></div></div>
          {breaks.length === 0 && <p className="field-hint">Nunca se ha registrado una diferencia para este contrato.</p>}
          {breaks.map((item) => <BreakCard key={item.id} item={item} walletId={wallet.id} />)}
        </section>
      </div>

      <section className="card">
        <div className="section-heading small"><div><span className="page-eyebrow">Libro</span><h2>Asientos ({journal.length})</h2></div></div>
        <div className="table-wrap">
          <table className="admin-table">
            <thead><tr><th>#</th><th>Fecha</th><th>Asiento</th><th>Importe</th><th>Movimientos</th><th>Actor</th><th>Hash</th></tr></thead>
            <tbody>
              {journal.map((entry) => <JournalRow key={entry.id} entry={entry} tripNames={tripNames} />)}
              {journal.length === 0 && <EmptyRow colSpan={7}>Sin asientos todavía.</EmptyRow>}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="section-heading small"><div><span className="page-eyebrow">Conciliaciones</span><h2>Corridas registradas ({runs.length})</h2></div></div>
        <p className="field-hint">Cada lectura de Kuspit compara su saldo con la suma de bolsillos. Una corrida que cuadra se guarda a lo más cada seis horas; toda otra corrida se guarda siempre.</p>
        <div className="table-wrap">
          <table className="admin-table">
            <thead><tr><th>Fecha</th><th>Resultado</th><th>Kuspit</th><th>Bolsillos</th><th>Diferencia</th><th>Rendimiento</th><th>Notas</th></tr></thead>
            <tbody>
              {runs.map((run) => {
                const mismatches = Array.isArray(run.details.internalMismatches) ? run.details.internalMismatches.length : 0;
                return (
                  <tr key={run.id} className={run.status === "break" || mismatches ? "row-bad" : undefined}>
                    <td>{formatTimestamp(run.createdAt)}</td>
                    <td><RunPill status={run.status} /></td>
                    <td translate="no">{money.format(run.provider)}</td>
                    <td translate="no">{money.format(run.ledger)}</td>
                    <td translate="no">{signed(run.difference)}</td>
                    <td translate="no">{run.yieldPosted ? money.format(run.yieldPosted) : "—"}</td>
                    <td>{mismatches ? <Pill tone="bad" label={`${mismatches} bolsillo(s) no coinciden con sus tablas`} /> : run.details.interestShortfall ? <Pill tone="warn" label={`rendimiento reportado a la baja ${money.format(Number(run.details.interestShortfall))}`} /> : <span className="muted">—</span>}</td>
                  </tr>
                );
              })}
              {runs.length === 0 && <EmptyRow colSpan={7}>Sin conciliaciones todavía.</EmptyRow>}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
}

function JournalRow({ entry, tripNames }: { entry: FundJournalEntry; tripNames: Map<string, string | null> }) {
  return (
    <tr>
      <td className="mono">{entry.sequence}</td>
      <td>{formatTimestamp(entry.occurredAt)}</td>
      <td><div className="table-primary"><strong>{KIND_LABELS[entry.kind] ?? entry.kind}</strong><span>{entry.description}</span></div></td>
      <td translate="no">{money.format(entry.amount)}</td>
      <td>
        <div className="table-primary">
          {entry.postings.map((posting, index) => (
            <span key={index} translate="no">{accountLabel(posting.account, tripNames)} {signed(posting.amount)} <span className="muted">→ {money.format(posting.balanceAfter)}</span></span>
          ))}
        </div>
      </td>
      <td>{entry.actorType}</td>
      <td className="mono" title={`previo ${entry.previousHash}`}>{entry.entryHash.slice(0, 12)}…</td>
    </tr>
  );
}

const TARGETS: Array<{ value: FundBreakTarget; label: string; hint: string }> = [
  { value: "libre", label: "Wallet Libre", hint: "Kuspit tiene más (o menos) por algo que la plataforma no vio: bonificación, comisión, corrección de Kuspit. La diferencia se aplica a Wallet Libre." },
  { value: "trip_settlement", label: "Pago de viaje", hint: "El dinero salió del contrato para pagar un viaje que Operaciones ya liquidó. Solo para diferencias negativas, y se toma del bolsillo 'aplicado'." },
  { value: "suspense", label: "Suspense", hint: "Aún no se entiende. Queda visible, excluido de lo que el cliente puede gastar, hasta aclararlo con Kuspit." }
];

function BreakCard({ item, walletId }: { item: FundBreak; walletId: string }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<FundBreakTarget>(item.difference < 0 ? "trip_settlement" : "libre");
  const [reason, setReason] = useState("");
  const [approval, setApproval] = useState("");
  const action = useAction();
  const invalidate = useInvalidateAll();
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    const ok = await action.run("resolve", async () => {
      await apiRequest(`/api/v1/admin/fondos/breaks/${item.id}/resolve`, { method: "POST", body: JSON.stringify({ target, reason, approvalReference: approval || undefined }) });
      await invalidate();
      return "Diferencia explicada con un ajuste; la próxima conciliación debe cuadrar.";
    });
    if (ok) setOpen(false);
  };
  return (
    <div className={`card nested ${item.status === "open" ? "row-bad" : ""}`}>
      <KeyValues rows={[
        { label: "Estado", value: <Pill tone={item.status === "open" ? "bad" : item.status === "resolved" ? "ok" : "info"} label={item.status === "open" ? "abierta" : item.status === "resolved" ? "resuelta con ajuste" : "se aclaró sola"} /> },
        { label: "Diferencia (Kuspit − bolsillos)", value: <strong>{signed(item.difference)}</strong> },
        { label: "Kuspit / bolsillos", value: `${money.format(item.provider)} / ${money.format(item.ledger)}` },
        { label: "Observada", value: `${formatTimestamp(item.firstSeenAt)} · ${item.occurrences} lectura(s), última ${relativeTime(item.lastSeenAt)}` },
        item.resolutionNote ? { label: "Explicación", value: item.resolutionNote } : null,
        item.resolvedAt ? { label: "Resuelta", value: formatTimestamp(item.resolvedAt) } : null,
        item.clearedAt ? { label: "Aclarada", value: formatTimestamp(item.clearedAt) } : null
      ]} />
      {item.status === "open" && (
        <div className="row-actions">
          <button type="button" className="primary-button small" onClick={() => { action.clear(); setOpen(true); }}>Explicar con un ajuste</button>
        </div>
      )}
      <Modal open={open} label="Explicar diferencia" onOpenChange={(next) => { if (action.busy === null) setOpen(next); }}>
        <form className="dialog-content" onSubmit={(event) => void submit(event)}>
          <h2>Explicar {signed(item.difference)}</h2>
          <div className="dialog-body">
            <p className="field-hint">Se registrará un asiento de ajuste por exactamente esta diferencia, con tu usuario, el motivo y la referencia de aprobación. El asiento es inmutable y queda en la bitácora.</p>
            <Field label="Destino" htmlFor={`target-${item.id}`} hint={TARGETS.find((option) => option.value === target)?.hint}>
              <select id={`target-${item.id}`} className="standalone-input" value={target} onChange={(event) => setTarget(event.target.value as FundBreakTarget)}>
                {TARGETS.map((option) => <option key={option.value} value={option.value} disabled={option.value === "trip_settlement" && item.difference > 0}>{option.label}</option>)}
              </select>
            </Field>
            <Field label="Motivo" htmlFor={`reason-${item.id}`}>
              <textarea id={`reason-${item.id}`} className="standalone-input" rows={3} value={reason} onChange={(event) => setReason(event.target.value)} minLength={5} maxLength={600} required placeholder="Qué explica la diferencia y cómo se comprobó" />
            </Field>
            <Field label="Referencia de aprobación (opcional)" htmlFor={`approval-${item.id}`}>
              <input id={`approval-${item.id}`} className="standalone-input" value={approval} onChange={(event) => setApproval(event.target.value)} maxLength={120} placeholder="Ticket, correo o folio" />
            </Field>
            <Feedback error={action.error} notice={null} />
          </div>
          <div className="dialog-actions">
            <button type="button" className="secondary-button" onClick={() => setOpen(false)} disabled={action.busy !== null}>Cancelar</button>
            <button type="submit" className="primary-button solid" disabled={action.busy !== null}>{action.busy === "resolve" ? <Spinner /> : null} Registrar ajuste</button>
          </div>
        </form>
      </Modal>
      <span className="muted" hidden>{walletId}</span>
    </div>
  );
}
